import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  Checks,
  Holds,
  MaintenanceJob,
  MaintenanceRunDetail,
  MaintenanceRunList,
  PassHold,
  Regressions,
  RetentionHold,
} from "./types";

export function useMaintenanceJobs() {
  return useQuery({
    queryKey: ["maintenance", "jobs"],
    queryFn: () => api.get<MaintenanceJob[]>("/api/maintenance/jobs"),
    // The set of repairs the build knows about changes on deploy, not during a session.
    staleTime: Infinity,
  });
}

export function useMaintenanceRuns() {
  return useQuery({
    queryKey: ["maintenance", "runs"],
    queryFn: () => api.get<MaintenanceRunList>("/api/maintenance/runs"),
  });
}

export function useMaintenanceRun(runId: number | null) {
  return useQuery({
    queryKey: ["maintenance", "run", runId],
    queryFn: () => api.get<MaintenanceRunDetail>(`/api/maintenance/runs/${runId}`),
    enabled: runId !== null,
  });
}

/** Everything a run touches: the ledger, and the rows it may have rewritten. */
function invalidateMaintenance(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ["maintenance"] });
  void client.invalidateQueries({ queryKey: ["segments"] });
  void client.invalidateQueries({ queryKey: ["timeline"] });
  void client.invalidateQueries({ queryKey: ["audit"] });
}

export function useCreateRun() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, params }: { kind: string; params?: Record<string, unknown> }) =>
      api.post<MaintenanceRunDetail>("/api/maintenance/runs", { kind, params: params ?? {} }),
    onSuccess: () => invalidateMaintenance(client),
  });
}

export function useApplyRun() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (runId: number) =>
      api.post<MaintenanceRunDetail>(`/api/maintenance/runs/${runId}/apply`),
    onSuccess: () => invalidateMaintenance(client),
  });
}

export function useCancelRun() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (runId: number) =>
      api.post<MaintenanceRunDetail>(`/api/maintenance/runs/${runId}/cancel`),
    onSuccess: () => invalidateMaintenance(client),
  });
}

export function useChecks() {
  return useQuery({
    queryKey: ["maintenance", "checks"],
    queryFn: () => api.get<Checks>("/api/maintenance/checks"),
    // Each one is a single indexed query, but a handful of them together is still real work
    // for the process serving audio. Half a minute is often enough for a screen somebody
    // opens when something is already wrong.
    staleTime: 30_000,
  });
}

export function useHolds() {
  return useQuery({
    queryKey: ["maintenance", "holds"],
    queryFn: () => api.get<Holds>("/api/maintenance/holds"),
  });
}

export function useHoldPass() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { pass_name: string; hours: number; reason?: string }) =>
      api.post<PassHold>("/api/maintenance/holds/pass", body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["maintenance", "holds"] }),
  });
}

export function useReleasePass() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (passName: string) => api.del<void>(`/api/maintenance/holds/pass/${passName}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["maintenance", "holds"] }),
  });
}

export function useHoldRetention() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { t_start: string; t_end: string; hours: number; reason?: string }) =>
      api.post<RetentionHold>("/api/maintenance/holds/retention", body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["maintenance", "holds"] }),
  });
}

export function useReleaseRetention() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (holdId: number) => api.del<void>(`/api/maintenance/holds/retention/${holdId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["maintenance", "holds"] }),
  });
}

export function useRegressions(includeDismissed = false) {
  return useQuery({
    queryKey: ["maintenance", "regressions", includeDismissed],
    queryFn: () =>
      api.get<Regressions>(
        `/api/maintenance/parser/regressions${includeDismissed ? "?include_dismissed=true" : ""}`,
      ),
  });
}

export function useMarkRegression() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { segment_id: number; verdict: string; note?: string }) =>
      api.post<unknown>("/api/maintenance/parser/marks", body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["maintenance", "regressions"] }),
  });
}
